import { useTranslation } from 'react-i18next';
import type { RoundTally } from '../../utils/round-score-summary';

interface BoulderTallyProps {
  tally: RoundTally;
  /**
   * Whether to show how many of the tops and zones were on judged blocks — the
   * board's regulation tells them apart (OPES). Shown even at 0, so who has no
   * judged top reads at a glance.
   */
  showJudged: boolean;
  /**
   * The attempts to the tops and to the zones the place is broken on, as the
   * server's ranking counted them (`rankingAttempts`), shown beside a share
   * that has at least one judged block in it. Null when the row has no score.
   */
  attempts?: { top: number; zone: number } | null;
  /** The wrapper's test id: the round's board and the standings page each have their own. */
  testId?: string;
}

/**
 * A boulder card set's "solved" cell: `3 Top · 5 Zone`, or under OPES
 * `3 Top (1G · A2) · 5 Zone (2G · A3)` — beside each share, the attempts the
 * place is broken on: to the tops, then to the zones, judged blocks only ("A"
 * for attempts: a "T" for tentativi would read as a top).
 *
 * **A top is a zone too** (#363), in the counts as in the ranking, so the zone
 * A has a zone count that agrees with it: Bruno's judged `T10` with the zone
 * on the 5th is `1 Top (1G · A10) · 1 Zone (1G · A5)`, and the climber with
 * the zone on the 9th reads `A9` — the number that put them behind.
 *
 * **An A only beside a share of at least 1G.** With no judged block in it there
 * are no attempts that count, and `1 Top (0G · A0)` reads as a top on no go at
 * all — so it is `1 Top (0G)`. The counts are display (`tally`); the attempts
 * are the server's own numbers — see the `competition-rankings` skill.
 */
export function BoulderTally({ tally, showJudged, attempts, testId = 'score-tally' }: BoulderTallyProps) {
  const { t } = useTranslation();

  const share = (count: number, attemptsCounted: number | undefined, kind: 'tops' | 'zones') => {
    if (!showJudged) return null;

    const tried = count > 0 ? attemptsCounted : undefined;
    return (
      <span
        className="text-muted-foreground"
        title={
          tried == null
            ? t(`competitionRankings.judgedShareHelp.${kind}`, { count })
            : t(`competitionRankings.judgedAttemptsHelp.${kind}`, { count, attempts: tried })
        }
        data-testid={`tally-judged-${kind}`}
      >
        {tried == null
          ? t('competitionRankings.judgedShare', { count })
          : t('competitionRankings.judgedShareAttempts', { count, attempts: tried })}
      </span>
    );
  };

  return (
    <span className="inline-flex items-center gap-3 text-xs" data-testid={testId}>
      <span className="inline-flex items-center gap-1">
        <span className="font-semibold tabular-nums text-green-600" data-testid="tally-tops">
          {tally.tops}
        </span>
        <span className="text-muted-foreground">{t('judgeBoulder.top')}</span>
        {share(tally.judgedTops, attempts?.top, 'tops')}
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="font-semibold tabular-nums text-yellow-600" data-testid="tally-zones">
          {tally.zones}
        </span>
        <span className="text-muted-foreground">{t('judgeBoulder.zone')}</span>
        {share(tally.judgedZones, attempts?.zone, 'zones')}
      </span>
    </span>
  );
}

export default BoulderTally;
